import { RefObject, useLayoutEffect } from "react";

// Horizontal table scrollports prevent CSS sticky from following the page.
// Move the original header within its table so sorting, selection, column
// widths and horizontal scrolling keep their existing owners.
export default function useStickyTableHeaders(containerRef: RefObject<HTMLElement>) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const offsets = new Map<HTMLElement, number>();
    let frame = 0;
    const update = () => {
      frame = 0;
      const top = container.getBoundingClientRect().top + (window.innerWidth < 768 ? 64 : 0);
      const heads = new Set<HTMLElement>(container.querySelectorAll("table > thead"));
      container.querySelectorAll('[role="columnheader"]').forEach((cell) => {
        const row = cell.closest<HTMLElement>('[role="row"]');
        if (row) heads.add(row);
      });
      heads.forEach((head) => {
        const table = head.closest('table, [role="table"]');
        if (!table) return;
        const rect = head.getBoundingClientRect();
        const naturalTop = rect.top - (offsets.get(head) || 0);
        const offset = Math.max(0, Math.min(top - naturalTop,
          table.getBoundingClientRect().bottom - rect.height - naturalTop));
        head.dataset.dashboardTableHeader = "";
        head.toggleAttribute("data-dashboard-table-stuck", offset > 0);
        head.style.setProperty("--dashboard-header-offset", `${offset}px`);
        offsets.set(head, offset);
      });
      offsets.forEach((_offset, head) => {
        if (!container.contains(head)) offsets.delete(head);
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(container, { childList: true, subtree: true });
    const resize = new ResizeObserver(schedule);
    resize.observe(container);
    container.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    update();
    return () => {
      observer.disconnect();
      resize.disconnect();
      cancelAnimationFrame(frame);
      container.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      offsets.forEach((_offset, head) => {
        delete head.dataset.dashboardTableHeader;
        head.removeAttribute("data-dashboard-table-stuck");
        head.style.removeProperty("--dashboard-header-offset");
      });
    };
  }, [containerRef]);
}
